import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { datadogLogs } from "@datadog/browser-logs";

/** What the API says about the token in an enrollment URL. */
export type EnrollmentTokenStatus = "Valid" | "Invalid" | "Expired" | "Completed";

/** How the debit amount is determined. Mirrors `AchAmountType` on the API. */
export type AchAmountType = "Fixed" | "Variable" | "VariableCapped";

/** The maximum cadence of new debits. Mirrors `AchMandateFrequency` on the API. */
export type AchMandateFrequency = "Weekly" | "BiWeekly" | "Monthly" | "Quarterly";

/** What ends the authorization. Mirrors `AchContractEndCondition` on the API. */
export type AchContractEndCondition = "OnCancel" | "OnEndDate" | "MaxDebits";

/** The mandate terms the customer is being asked to authorize. */
export interface EnrollmentTerms {
  amountType: AchAmountType;
  /** Fixed only. */
  amount: number | null;
  /** VariableCapped only. */
  maxAmount: number | null;
  frequency: AchMandateFrequency;
  /** Date only, no offset — e.g. `2026-11-01T00:00:00`. */
  startDate: string;
  /**
   * What ends the authorization. Null only for links created before it was captured; the end is
   * then described from `endDate` and `maxDebits`.
   */
  contractEndCondition: AchContractEndCondition | null;
  /** Date only, no offset. Null when the authorization has no end date. */
  endDate: string | null;
  /** Null when the number of debits is unlimited. */
  maxDebits: number | null;
  currency: string;
}

/** The dealer details printed on the consent. Each is null only for links created before it was captured. */
export interface EnrollmentDealer {
  dealerName: string | null;
  dealerPhone: string | null;
  paymentReceiverName: string | null;
}

export interface ValidateEnrollmentTokenRequest {
  token: string;
}

/** What the bank-account step needs to mount Adyen's ACH component. */
export interface EnrollmentAdyenConfig {
  clientKey: string;
  /** `test` or `live`. */
  environment: string;
}

export type BankAccountType = "checking" | "savings";
export type AccountHolderType = "personal" | "business";

/**
 * A bank account the customer saved on an earlier enrollment, offered for reuse (182095). Only what the
 * customer can already see: no bank name, no holder name, no token.
 */
export interface EnrollmentSavedAccount {
  id: string;
  accountType: BankAccountType;
  accountHolderType: AccountHolderType;
  /** May be empty when Adyen did not report the last four digits. */
  last4: string;
}

export interface ValidateEnrollmentTokenResponse {
  status: EnrollmentTokenStatus;
  /** UTC, with offset. Populated only when Valid. */
  expiresAt: string | null;
  /** Populated only when Valid. */
  terms: EnrollmentTerms | null;
  /** Populated only when Valid. */
  dealer: EnrollmentDealer | null;
  /** Populated only when Valid; null if the server has no Adyen client key configured. */
  adyen: EnrollmentAdyenConfig | null;
  /** The customer's reusable saved accounts, newest first (182095). Empty unless Valid. */
  savedAccounts: EnrollmentSavedAccount[];
}

/** Adyen's encrypted bank details, exactly as its ACH component produced them. */
export interface EnrollmentBankDetails {
  encryptedBankAccountNumber: string;
  encryptedBankLocationId: string;
  ownerName: string;
}

/** The kind of a consent block, which decides which of its fields are set. Mirrors `ConsentBlockType` on the API. */
export type ConsentBlockType =
  | "Title"
  | "Paragraph"
  | "Field"
  | "FieldGroup"
  | "Checkboxes"
  | "Signature"
  | "Acceptance";

/** A run of a paragraph's text; `filled` marks a value filled in for the customer. */
export interface ConsentRun {
  text: string;
  filled: boolean;
}

export interface ConsentField {
  label: string;
  value: string;
}

export interface ConsentChoice {
  label: string;
  checked: boolean;
}

/** One piece of the consent, in the order it is shown and printed in the signed PDF. */
export interface ConsentBlock {
  type: ConsentBlockType;
  /** Title, Checkboxes (the text after the ticks), Acceptance. */
  text?: string | null;
  /** Paragraph. */
  runs?: ConsentRun[] | null;
  /** Field, Signature. */
  label?: string | null;
  /** Field. */
  value?: string | null;
  /** FieldGroup. */
  fields?: ConsentField[] | null;
  /** Checkboxes. */
  choices?: ConsentChoice[] | null;
}

/**
 * The consent the customer signs, built on the server (183299) so the page and the signed PDF show the same
 * wording. `contentHash` is sent back unchanged when signing; the text never is.
 */
export interface ConsentView {
  templateVersion: string;
  blocks: ConsentBlock[];
  /** 64 lowercase hex characters. */
  contentHash: string;
}

export interface GetEnrollmentConsentRequest {
  token: string;
  /** The box ticked on the form. */
  accountType: BankAccountType;
  /** A new account's holder name: the receiver's name when the dealer sent none. Omitted for a saved account. */
  holderName?: string;
  /** The browser's IANA time zone, for the form's date. Sent again, unchanged, when signing. */
  timeZone: string;
}

export interface GetEnrollmentConsentResponse {
  status: EnrollmentTokenStatus;
  /** Populated only when Valid. */
  consent: ConsentView | null;
}

interface SignMandateRequestBase {
  token: string;
  signature: string;
  consentAgreed: boolean;
  /** `ConsentView.contentHash` of the consent the customer read and signed. */
  contentHash: string;
  /** The same time zone the consent was fetched with. */
  timeZone: string;
}

/** Signing with a new bank account: Adyen verifies and saves it in the same call. */
export interface SignMandateWithNewAccountRequest extends SignMandateRequestBase {
  paymentMethod: EnrollmentBankDetails;
  bankAccountType: BankAccountType;
  accountHolderType: AccountHolderType;
  ccdAcknowledged: boolean;
}

/**
 * Signing with a saved account (182095). No bank details: the account was verified when it was first
 * saved, and its type and CCD acknowledgement come from the server.
 */
export interface SignMandateWithSavedAccountRequest extends SignMandateRequestBase {
  vaultedAccountId: string;
}

/** A new account or a saved one — the API rejects a request with both. */
export type SignMandateRequest = SignMandateWithNewAccountRequest | SignMandateWithSavedAccountRequest;

/** The outcome of signing. Every value has its own screen on the result step. */
export type SignMandateStatus =
  | "Completed"
  | "VerificationFailed"
  | "ReEnterDetails"
  | "LinkUnusable"
  | "TryAgain"
  | "SavedAccountUnavailable"
  /**
   * The consent changed while the page was open (183299). Nothing was saved and nothing was sent to the bank: the
   * sign step shows the current consent to sign again.
   */
  | "ConsentChanged";

export interface SignMandateResponse {
  status: SignMandateStatus;
  /** Populated only when LinkUnusable. */
  linkStatus: EnrollmentTokenStatus | null;
  /** Populated only when Completed. */
  account: { accountType: BankAccountType; accountHolderType: AccountHolderType; last4: string } | null;
  /** UTC, with offset. Populated only when Completed. */
  signedAt: string | null;
}

/** Whether the customer's signed authorization PDF can be opened now (182148). Mirrors `SignedConsentStatus` on the API. */
export type SignedConsentStatus = "Ready" | "Pending" | "Unavailable" | "Invalid";

export interface GetConsentDocumentRequest {
  token: string;
}

export interface GetConsentDocumentResponse {
  status: SignedConsentStatus;
  /** 64 lowercase hex characters. Populated only when Ready. */
  ticket: string | null;
  /** UTC, with offset. Populated only when Ready. */
  expiresAtUtc: string | null;
}

/**
 * The API address that opens the signed PDF for a ticket from `getConsentDocument` (182148). Built from the page's
 * own API base, so the API never needs to know its public address. The ticket is not the token: it opens one PDF,
 * for 5 minutes.
 */
export const consentDocumentUrl = (ticket: string) =>
  `${process.env.REACT_APP_BASE_URL}ACHRegistration/ConsentDocument/${encodeURIComponent(ticket)}`;

/**
 * Base query for the public ACH enrollment page.
 *
 * @remarks
 * Deliberately NOT the shared `baseQueryWithRefreshTokenHandler`. That one attaches the logged-in
 * user's JWT or API key, and on a 401 redirects to `/login` — both wrong here. The customer is
 * anonymous, the enrollment token is their only credential, and it travels in the request body.
 * `credentials: "omit"` keeps any PerseusPay cookies a dealer user might have in the same browser
 * off these calls.
 */
const publicBaseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BASE_URL,
  credentials: "omit",
  prepareHeaders: (headers) => {
    headers.set("Content-Type", "application/json");
    headers.set("Accept", "application/json");
    return headers;
  },
});

/**
 * Logs failed calls the same way `authFetchBaseQuery` does, minus the response body. The request
 * body carries the token, so nothing from either side of the exchange is logged — only the endpoint
 * and status.
 */
const publicBaseQueryWithLogging: typeof publicBaseQuery = async (args, api, extraOptions) => {
  const result = await publicBaseQuery(args, api, extraOptions);
  if (result.error) {
    datadogLogs.logger.error("Public API Error", {
      httpStatus: result.error.status,
      endpoint: typeof args === "string" ? args : args.url,
      method: typeof args === "string" ? "GET" : args.method ?? "GET",
    });
  }
  return result;
};

/**
 * RTK Query slice for the anonymous endpoints behind the public ACH enrollment page
 * (`ACHRegistrationController`, anonymous as a whole).
 *
 * @remarks
 * Excluded from redux-persist in `app/store.ts`: query args include the token, and responses carry
 * the mandate terms — neither should outlive the tab in localStorage, least of all on a shared
 * computer.
 */
export const achEnrollmentPublicApi = createApi({
  reducerPath: "achEnrollmentPublicApi",
  baseQuery: publicBaseQueryWithLogging,
  refetchOnMountOrArgChange: true,
  keepUnusedDataFor: 0,

  endpoints: (builder) => ({
    /**
     * Checks the token from an enrollment URL before the page loads.
     *
     * @remarks
     * A POST used as a query, like `validateSignature`: the token goes in the body so it stays out of
     * URL-based telemetry. Every outcome comes back as HTTP 200 with a `status`; only a network or
     * server failure surfaces as `error`.
     */
    validateEnrollmentToken: builder.query<ValidateEnrollmentTokenResponse, ValidateEnrollmentTokenRequest>({
      query: (body) => ({
        url: "ACHRegistration/ValidateToken",
        method: "POST",
        body,
      }),
    }),

    /**
     * The consent to sign, for the account the customer chose (183299).
     *
     * @remarks
     * A POST used as a query, like `validateEnrollmentToken`: the token goes in the body. Never cached
     * (`keepUnusedDataFor: 0`), so going back and choosing another account fetches it again.
     */
    getEnrollmentConsent: builder.query<GetEnrollmentConsentResponse, GetEnrollmentConsentRequest>({
      query: (body) => ({
        url: "ACHRegistration/Consent",
        method: "POST",
        body,
      }),
    }),

    /**
     * The customer signs. The server verifies and saves the bank account in the same call, and the
     * authorization only takes effect if that succeeds.
     *
     * @remarks
     * A mutation, so nothing about it is cached: the body holds the encrypted bank details, which
     * are single-use and must not be replayed.
     */
    signMandate: builder.mutation<SignMandateResponse, SignMandateRequest>({
      query: (body) => ({
        url: "ACHRegistration/SignMandate",
        method: "POST",
        body,
      }),
    }),

    /**
     * A 5-minute ticket to the customer's signed authorization PDF (182148), or why there is none yet.
     *
     * @remarks
     * A POST used as a query, like `getEnrollmentConsent`: the token goes in the body. Never cached
     * (`keepUnusedDataFor: 0`), so every ask gets a new ticket.
     */
    getConsentDocument: builder.query<GetConsentDocumentResponse, GetConsentDocumentRequest>({
      query: (body) => ({
        url: "ACHRegistration/ConsentDocument",
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useValidateEnrollmentTokenQuery,
  useGetEnrollmentConsentQuery,
  useSignMandateMutation,
  useGetConsentDocumentQuery,
} = achEnrollmentPublicApi;
